import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { AppContext } from '../../context/AppContext';
import { stopAllMusic } from '../../components/MusicPlayer';
import CouplesNames from './CouplesNames';
import CouplesTemplate from './CouplesTemplate';
import CouplesMessage from './CouplesMessage';
import CouplesPhoto from './CouplesPhoto';
import CouplesMusic from './CouplesMusic';
import CouplesDatePlan from './CouplesDatePlan';
import CouplesPreview from './CouplesPreview';

const STEPS = ['Names', 'Template', 'Message', 'Photo', 'Music', 'Date Plan', 'Preview'];

const CreateCouples = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const { getInvitationByToken } = useContext(AppContext);
  const editMode = !!token;

  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    senderName: '',
    recipientName: '',
    templateId: '',
    templateName: '',
    message: '',
    photoUrl: '',
    musicTrack: '',
    musicTitle: '',
    eventDate: '',
    eventTime: '',
    eventLocation: '',
    includeDatePlan: false,
  });

  useEffect(() => {
    if (editMode && token) {
      const loadExisting = async () => {
        const inv = await getInvitationByToken(token);
        if (inv) {
          setData({
            senderName: inv.sender_display_name || '',
            recipientName: inv.recipient_name || '',
            templateId: inv.template_id || '',
            templateName: inv.occasion || '',
            message: inv.message || '',
            photoUrl: inv.photo_url || '',
            musicTrack: inv.music_track || '',
            musicTitle: inv.music_title || '',
            eventDate: inv.event_date || '',
            eventTime: inv.event_time || '',
            eventLocation: inv.event_location || '',
            includeDatePlan: !!inv.event_date,
          });
        }
      };
      loadExisting();
    }
  }, [editMode, token, getInvitationByToken]);

  // Stop all music when leaving the creation flow entirely
  useEffect(() => {
    return () => {
      stopAllMusic();
    };
  }, []);

  const handleNext = (stepData) => {
    stopAllMusic();
    setData(prev => ({ ...prev, ...stepData }));
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    stopAllMusic();
    if (step > 0) setStep(prev => prev - 1);
    else navigate('/home');
  };

  const handleGoToStep = (targetStep) => {
    stopAllMusic();
    setStep(targetStep);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-white px-4 py-4 flex items-center shadow-sm z-10 sticky top-0">
        <button onClick={handleBack} className="p-2 -ml-2 text-gray-600">
          <SafeIcon name="ChevronLeft" className="text-2xl" />
        </button>
        <div className="flex-1 text-center">
          <p className="font-semibold text-gray-800 text-sm">
            {editMode ? '✏️ Editing' : STEPS[step]}
          </p>
          <p className="text-[10px] text-gray-400">Step {step + 1} of {STEPS.length}</p>
        </div>
        <div className="w-10" />
      </div>
      <div className="w-full bg-gray-200 h-1">
        <div
          className="bg-gradient-to-r from-purple-500 to-violet-500 h-1 transition-all duration-500"
          style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
        />
      </div>
      <div className="flex-1 overflow-y-auto">
        {step === 0 && <CouplesNames data={data} onNext={handleNext} />}
        {step === 1 && <CouplesTemplate data={data} onNext={handleNext} />}
        {step === 2 && <CouplesMessage data={data} onNext={handleNext} />}
        {step === 3 && <CouplesPhoto data={data} onNext={handleNext} />}
        {step === 4 && <CouplesMusic data={data} onNext={handleNext} />}
        {step === 5 && <CouplesDatePlan data={data} onNext={handleNext} />}
        {step === 6 && (
          <CouplesPreview
            data={data}
            onBack={handleBack}
            editMode={editMode}
            editToken={token}
            onGoToStep={handleGoToStep}
          />
        )}
      </div>
    </div>
  );
};

export default CreateCouples;